import toast from "react-hot-toast";
import { confirmDialog } from '@/lib/confirmDialog';
import { reportApiError } from '@/common/utils/apiError';
import { AlertProps, QuestionAlertProps, UseAlertReturn } from '../types';

type AlertType = 'error' | 'info' | 'success' | 'warning';

export const useAlert = (): UseAlertReturn => {
  const baseAlert = ({ type, title, message, exception }: AlertProps & { type: AlertType }): void => {
    const content = message || exception?.message || '';
    const displayMessage = content ? `${title || ''}: ${content}`.trim().replace(/^:/, '') : title || '';

    switch (type) {
      case 'error':
        toast.error(displayMessage || 'Error');
        break;
      case 'success':
        toast.success(displayMessage || 'Éxito');
        break;
      case 'warning':
        toast(displayMessage || 'Advertencia', { icon: '⚠️' });
        break;
      case 'info':
      default:
        toast(displayMessage || 'Información', { icon: 'ℹ️' });
        break;
    }
  };

  /**
   * El mensaje mostrado sale de `describeApiError`, que traduce el status a algo
   * accionable. Antes se usaba `exception.message`, o sea textos de axios como
   * "Request failed with status code 429", que no le dicen nada al usuario.
   */
  const errorAlert = ({ exception, message, ...props }: AlertProps): void => {
    if (!exception) {
      baseAlert({ ...props, message, type: 'error' });
      return;
    }

    const info = reportApiError(exception, {
      operation: props.title ?? 'accion-usuario',
    });

    // Una request cancelada (el usuario cambió de pantalla) no es un error
    if (info.isCanceled) return;

    baseAlert({
      ...props,
      type: 'error',
      message: message || info.detail || info.message,
    });
  };

  const successAlert = (props: AlertProps): void => baseAlert({ ...props, type: 'success' });
  const warningAlert = (props: AlertProps): void => baseAlert({ ...props, type: 'warning' });
  const infoAlert = (props: AlertProps): void => baseAlert({ ...props, type: 'info' });

  const questionAlert = async ({
    title,
    text,
    confirmButtonText = "OK",
    cancelButtonText = "Cancelar"
  }: QuestionAlertProps): Promise<{ isConfirmed: boolean }> => {
    const confirmed = await confirmDialog.open({ title, text, confirmButtonText, cancelButtonText });
    return { isConfirmed: confirmed };
  };

  return {
    errorAlert,
    successAlert,
    warningAlert,
    infoAlert,
    questionAlert
  };
};
